import axiosInstance from "./axios";
import {
  AddressTicketPayload,
  CreateTicketPayload,
  TicketDetailsResult,
  TicketItem,
  TicketListQuery,
  TicketMessage,
  TicketPagination,
  UpdateTicketPayload,
} from "../types/ticket";

interface TicketListResult {
  tickets: TicketItem[];
  pagination: TicketPagination | null;
}

const extractPayload = (response: any) => response?.data?.data ?? response?.data ?? {};

const extractDataNode = (payload: any) => payload?.data ?? payload;

const extractPagination = (payload: any): TicketPagination | null => {
  const pagination = payload?.pagination;
  if (!pagination) return null;

  return {
    offset: Number(pagination.offset ?? 0),
    limit: Number(pagination.limit ?? pagination.per_page ?? 10),
    page: Number(pagination.page ?? pagination.current_page ?? 1),
    total_pages: Number(pagination.total_pages ?? 1),
    total_count: Number(pagination.total_count ?? pagination.total ?? 0),
    has_next_page: Boolean(pagination.has_next_page),
    has_prev_page: Boolean(pagination.has_prev_page),
    next_page: pagination.next_page ?? null,
    prev_page: pagination.prev_page ?? null,
  };
};

const extractTicketList = (payload: any): TicketItem[] => {
  const node = extractDataNode(payload);
  if (Array.isArray(node)) return node;
  if (Array.isArray(node?.tickets)) return node.tickets;
  if (Array.isArray(node?.items)) return node.items;
  if (Array.isArray(node?.rows)) return node.rows;
  return [];
};

const extractMessages = (payload: any): TicketMessage[] => {
  const node = extractDataNode(payload);
  if (Array.isArray(node)) return node;
  if (Array.isArray(node?.messages)) return node.messages;
  if (Array.isArray(node?.ticket_messages)) return node.ticket_messages;
  if (Array.isArray(node?.items)) return node.items;
  return [];
};

export const createTicket = async (payload: CreateTicketPayload): Promise<TicketItem | null> => {
  const response = await axiosInstance.post("/tickets", payload);
  const body = extractDataNode(extractPayload(response));
  return body?.ticket ?? body ?? null;
};

export const getMyTickets = async (params: TicketListQuery): Promise<TicketListResult> => {
  const response = await axiosInstance.get("/tickets/my", { params });
  const payload = extractPayload(response);
  return {
    tickets: extractTicketList(payload),
    pagination: extractPagination(payload),
  };
};

export const getAllTickets = async (params: TicketListQuery): Promise<TicketListResult> => {
  const response = await axiosInstance.get("/tickets/all", { params });
  const payload = extractPayload(response);
  return {
    tickets: extractTicketList(payload),
    pagination: extractPagination(payload),
  };
};

export const getTicketById = async (
  ticketId: number,
  params?: Pick<TicketListQuery, "page" | "limit">
): Promise<TicketDetailsResult> => {
  const response = await axiosInstance.get(`/tickets/${ticketId}`, { params });
  const payload = extractPayload(response);
  const node = extractDataNode(payload);

  return {
    ticket: node?.ticket ?? node ?? null,
    messages: extractMessages(payload),
    pagination: extractPagination(payload),
  };
};

export const updateTicketDetails = async (ticketId: number, payload: UpdateTicketPayload) => {
  await axiosInstance.patch(`/tickets/${ticketId}`, payload);
};

export const addressTicket = async (ticketId: number, payload: AddressTicketPayload) => {
  await axiosInstance.patch(`/tickets/${ticketId}/address`, payload);
};

export const updateTicketStatus = async (ticketId: number, payload: AddressTicketPayload) => {
  await axiosInstance.patch(`/tickets/${ticketId}/status`, payload);
};

export const addTicketMessage = async (ticketId: number, message: string) => {
  await axiosInstance.post(`/tickets/${ticketId}/messages`, { message });
};

export const getTicketMessages = async (
  ticketId: number,
  params?: Pick<TicketListQuery, "page" | "limit">
): Promise<{ messages: TicketMessage[]; pagination: TicketPagination | null }> => {
  const response = await axiosInstance.get(`/tickets/${ticketId}/messages`, { params });
  const payload = extractPayload(response);

  return {
    messages: extractMessages(payload),
    pagination: extractPagination(payload),
  };
};
